import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MdCheckCircle, MdWarning, MdPhotoCamera } from 'react-icons/md';

import {
  releaseRequest,
  closeValidationModal,
  resetValidation,
  uploadPhotoRequest,
} from '~/store/modules/pickupValidation/actions';
import {
  StatusMessage,
  CourierCard,
  CourierInfo,
  Button,
  ModalFooter,
} from '../styles';

export default function StepReleaseConfirm({ cartId, released = false }) {
  const dispatch = useDispatch();
  const { validation, loadingRelease, loadingPhoto } = useSelector(
    state => state.pickupValidation
  );

  const [imageModalOpen, setImageModalOpen] = useState(false);

  const handleImageClick = useCallback(e => {
    e.preventDefault();
    setImageModalOpen(true);
  }, []);

  const handleImageClose = useCallback(() => {
    setImageModalOpen(false);
  }, []);

  useEffect(() => {
    if (!imageModalOpen) return undefined;

    const onKeyDown = e => {
      if (e.key === 'Escape') setImageModalOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [imageModalOpen]);

  const { courier } = validation;
  const fileInputRef = useRef(null);
  const photoUrl = validation.pickup_photo_url;

  const handleRelease = useCallback(() => {
    dispatch(releaseRequest(cartId));
  }, [dispatch, cartId]);

  const handleFinish = useCallback(() => {
    dispatch(closeValidationModal());
    dispatch(resetValidation());
  }, [dispatch]);

  const handlePhotoClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handlePhotoChange = useCallback(
    e => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      dispatch(uploadPhotoRequest(cartId, file));
      e.target.value = '';
    },
    [dispatch, cartId]
  );

  if (
    released ||
    validation.status === 'released' ||
    validation.status === 'canceled'
  ) {
    const isCanceled = validation.status === 'canceled';
    const isAnomaly = isCanceled && validation.skipped_validation;

    let title;
    let subtitle;
    let icon;
    let variant;

    if (isAnomaly) {
      variant = 'warning';
      icon = <MdWarning size={48} />;
      title = 'Sessão encerrada';
      subtitle =
        'O pedido foi finalizado sem que a validação fosse concluída. A ocorrência foi registrada.';
    } else if (isCanceled) {
      variant = 'warning';
      icon = <MdWarning size={48} />;
      title = 'Entrega cancelada';
      subtitle =
        'A entrega foi cancelada. A sessão de validação foi encerrada automaticamente.';
    } else {
      variant = 'success';
      icon = <MdCheckCircle size={48} />;
      title = 'Pedido entregue!';
      subtitle = `A mercadoria foi liberada para ${
        courier?.name || 'o entregador'
      }.`;
    }

    return (
      <>
        <StatusMessage variant={variant}>
          {icon}
          <div className="title">{title}</div>
          <div className="subtitle">{subtitle}</div>
        </StatusMessage>
        <ModalFooter style={{ padding: '16px 0 0', border: 0 }}>
          <Button type="button" variant="ghost" onClick={handleFinish}>
            Fechar
          </Button>
        </ModalFooter>
      </>
    );
  }

  return (
    <>
      <StatusMessage variant="success">
        <MdCheckCircle size={48} />
        <div className="title">Entregador confirmado</div>
        <div className="subtitle">
          O entregador iniciou a corrida. Pode entregar o pedido.
        </div>
      </StatusMessage>

      <CourierCard>
        <CourierInfo>
          <div className="name">{courier.name}</div>
          {courier.plate && <span className="plate">{courier.plate}</span>}
        </CourierInfo>
      </CourierCard>

      {/* Input de upload escondido */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handlePhotoChange}
      />

      {/* Thumbnail card (ou botão de anexar quando ainda não há foto) */}
      {photoUrl ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 12,
            padding: 10,
            border: '1px solid #e2e2e2',
            borderRadius: 8,
          }}
        >
          <a
            href={photoUrl}
            onClick={handleImageClick}
            title="Clique para ampliar a foto"
            style={{
              display: 'inline-block',
              lineHeight: 0,
              cursor: 'zoom-in',
            }}
          >
            <img
              src={photoUrl}
              alt="Foto da retirada"
              style={{
                width: 56,
                height: 56,
                objectFit: 'cover',
                borderRadius: 6,
              }}
            />
          </a>
          <div style={{ flex: 1, fontSize: 13, color: '#555' }}>
            Foto anexada à validação.
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              Clique na imagem para ampliar.
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={handlePhotoClick}
            disabled={loadingPhoto}
          >
            Trocar
          </Button>
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <Button
            type="button"
            variant="ghost"
            onClick={handlePhotoClick}
            disabled={loadingPhoto}
          >
            <MdPhotoCamera size={16} style={{ verticalAlign: 'middle' }} />{' '}
            {loadingPhoto
              ? 'Enviando foto...'
              : 'Anexar foto da retirada (opcional)'}
          </Button>
        </div>
      )}

      <ModalFooter style={{ padding: '8px 0 0', border: 0 }}>
        <Button
          type="button"
          variant="success"
          onClick={handleRelease}
          disabled={loadingRelease || loadingPhoto}
        >
          {loadingRelease ? 'Confirmando...' : 'Confirmar entrega do pedido'}
        </Button>
      </ModalFooter>

      {/* Lightbox overlay (renderiza fora do fluxo normal, por cima do modal) */}
      {imageModalOpen && photoUrl && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Fechar visualização"
          onClick={e => {
            if (e.target === e.currentTarget) handleImageClose();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') handleImageClose();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 20,
            cursor: 'pointer',
            border: 'none',
          }}
        >
          <button
            type="button"
            onClick={handleImageClose}
            aria-label="Fechar"
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255, 255, 255, 0.15)',
              color: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 40,
              height: 40,
              fontSize: 24,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
          <img
            src={photoUrl}
            alt="Foto da retirada (ampliada)"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 8,
              cursor: 'default',
              boxShadow: '0 4px 30px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}
    </>
  );
}
